import { useQuery } from '@tanstack/react-query';
import { Layers } from 'lucide-react';
import { CATEGORY_DOT_COLORS } from '@/lib/pattern-categories';
import { getCrossProjectIntelligence } from '@/lib/api';

/**
 * CategoryBreakdownPanel
 *
 * Shows category distribution across portfolio with mini progress bars
 */
export function CategoryBreakdownPanel(): React.ReactElement | null {
  const { data, isLoading } = useQuery({
    queryKey: ['intelligence'],
    queryFn: getCrossProjectIntelligence,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return <div className="text-xs text-text-secondary">Loading categories...</div>;
  }

  if (!data) {
    return null;
  }

  const breakdown = Object.entries(data.categoryBreakdown).sort((a, b) => b[1] - a[1]);
  const total = Object.values(data.categoryBreakdown).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary flex items-center gap-1">
        <Layers className="h-3.5 w-3.5" />
        Categories
      </div>

      {breakdown.length > 0 ? (
        <div className="space-y-1.5">
          {breakdown.map(([category, count]) => {
            const percentage = total > 0 ? (count / total) * 100 : 0;
            return (
              <div key={category} className="space-y-0.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <div
                      className={`h-2 w-2 flex-shrink-0 rounded-full ${CATEGORY_DOT_COLORS[category] ?? 'bg-border'}`}
                    />
                    <span className="text-[10px] text-text-secondary truncate">
                      {category.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <span className="text-[9px] text-text-tertiary ml-1 flex-shrink-0">{count}</span>
                </div>
                <div className="h-1 bg-surface-elevated rounded-full overflow-hidden">
                  <div
                    className={`h-full ${CATEGORY_DOT_COLORS[category] ?? 'bg-border'} transition-all`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded bg-surface-elevated/50 p-2 text-center text-[10px] text-text-tertiary">
          No categories yet
        </div>
      )}
    </div>
  );
}
